<template>
  <div class="chat-sidebar">
    <div class="chat-sidebar-header">
      <h6 class="chat-sidebar-title">Chat</h6>
    </div>
    <div class="chat-messages" ref="chatMessages">
      <div v-for="(message, index) in displayMessages" :key="index" class="chat-message-wrapper" :class="{ 'user-message-wrapper': message.role === 'user', 'ai-message-wrapper': message.role === 'ai' }">
        <!-- AI Message - Left Aligned -->
        <div v-if="message.role === 'ai'" class="chat-message ai-message">
          <div class="chat-message-avatar ai-avatar">
            <img :src="aiLogo" alt="AI" />
          </div>
          <div class="chat-message-content">
            <div class="chat-message-text">{{ message.text }}</div>
            <div class="chat-message-actions">
              <button class="chat-action-icon" @click="handleLike(index)">
                <img :src="likeIcon" alt="Like" />
              </button>
              <button class="chat-action-icon" @click="handleDislike(index)">
                <img :src="dislikeIcon" alt="Dislike" />
              </button>
            </div>
          </div>
        </div>
        <!-- User Message - Right Aligned -->
        <div v-if="message.role === 'user'" class="chat-message user-message">
          <div class="chat-message-content">
            <div class="chat-message-text">{{ message.text }}</div>
          </div>
          <div class="chat-message-avatar user-avatar">
            <span>{{ userInitials }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="chat-input-section">
      <div class="chat-input-box chat-sidebar-input-box">
        <div class="chat-input-content">
          <textarea
            id="agentic-dashboard-chat-composer"
            v-model="inputMessage"
            class="chat-input-field chat-sidebar-input-field chat-sidebar-textarea"
            placeholder="Let's create a dashboard....."
            @keydown="handleKeyPress"
            @input="handleInput"
            @click="handleInputClick"
            @focus="handleInputFocus"
            ref="chatInput"
            rows="3"
            tabindex="0"
          ></textarea>
        </div>
        <div class="chat-input-actions">
          <button 
            v-if="canEdit"
            class="chat-action-btn visual-edit-btn" 
            :class="{ 'active': selectedChartIndex !== null }"
            @click="handleVisualEdit" 
            type="button"
          >
            <img :src="visualEditIcon" alt="Visual Edit" class="visual-edit-icon" />
            <span class="visual-edit-text">Visual edit</span>
          </button>
          <button class="chat-action-btn send-btn" @click.stop="sendMessage" type="button" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate color="primary" size="20" width="2"></v-progress-circular>
            <img v-else :src="sendIcon" alt="Send" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ChatSidebar",
  props: {
    /** Session chat history from parent (dashboard or per-plot); parent owns and updates it */
    messages: {
      type: Array,
      default: () => [],
    },
    initialMessage: {
      type: String,
      default: null,
    },
    aiResponse: {
      type: String,
      default: null,
    },
    selectedChartIndex: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      inputMessage: "",
      aiLogo: require("@/assets/Images/FDPal/aiLogo.png"),
      likeIcon: require("@/assets/Images/FDPal/like.svg"),
      dislikeIcon: require("@/assets/Images/FDPal/dislike.svg"),
      sendIcon: require("@/assets/Images/FDPal/send-2.svg"),
      visualEditIcon: require("@/assets/Images/Dashboard/visual-edit-icon.svg"), // Placeholder - user will provide icon
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser"]),
    displayMessages() {
      return Array.isArray(this.messages) && this.messages.length > 0 ? this.messages : [];
    },
    userInitials() {
      const user = this.getUser;
      if (user && user.firstName && user.lastName) {
        return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
      } else if (user && user.username) {
        return user.username.charAt(0).toUpperCase();
      }
      return "U";
    },
  },
  mounted() {
    this.focusInput();
  },
  watch: {
    displayMessages() {
      this.$nextTick(() => this.scrollToBottom());
    },
  },
  methods: {
    sendMessage() {
      const message = this.inputMessage.trim();
      if (message) {
        const userMessage = message;
        this.inputMessage = "";
        // Parent appends to session history and handles API
        this.$emit("send-message", userMessage);
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    handleKeyPress(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    handleVisualEdit() {
      this.$emit("visual-edit");
    },
    handleLike(index) {
      this.$emit("message-like", index);
    },
    handleDislike(index) {
      this.$emit("message-dislike", index);
    },
    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages;
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    },
    handleInput(event) {
      console.log("Input changed:", event.target.value);
      this.inputMessage = event.target.value;
    },
    handleInputClick(event) {
      console.log("Input clicked");
      event.stopPropagation();
      if (this.$refs.chatInput) {
        this.$refs.chatInput.focus();
      }
    },
    handleInputFocus(event) {
      console.log("Input focused");
      event.stopPropagation();
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.chatInput) {
          this.$refs.chatInput.focus();
          console.log("Input focused programmatically");
        } else {
          console.log("Input ref not found");
        }
      });
    },
  },
};
</script>
